import { useEffect, useState, useCallback } from 'react';
import { logger } from '../utils/logger';
import { BarChart3, TrendingUp, Users, Music, Video, ShoppingBag, Coins, DollarSign, Eye, PlayCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ icon: Icon, label, value, subValue, color }) => (
  <div className="glass-card p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
  </div>
);

export default function AnalyticsPage() {
  const { isAuthenticated } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/analytics`);
      setAnalytics(res.data);
    } catch (error) {
      logger.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAnalytics();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchAnalytics]);

  if (!isAuthenticated) {
    return (
      <div className="main-content pt-24">
        <div className="container py-12">
          <div className="empty-state glass-card py-16">
            <BarChart3 className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">Sign in Required</h3>
            <p className="text-gray-400">Please sign in to view analytics</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="main-content pt-24">
        <div className="container py-12">
          <div className="empty-state glass-card py-16">
            <BarChart3 className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">Analytics Unavailable</h3>
            <p className="text-gray-400">Unable to load analytics data</p>
          </div>
        </div>
      </div>
    );
  }

  const { revenue, users, content, engagement, tokens, top_tracks, top_merch } = analytics;

  return (
    <div className="main-content pt-24" data-testid="analytics-page">
      <div className="container py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
            <p className="text-gray-400">Track your platform performance</p>
          </div>
        </div>

        {/* Revenue Overview */}
        <div className="glass-card p-6 mb-8" data-testid="revenue-overview">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" /> Revenue Overview
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-500/30">
              <p className="text-gray-400 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold text-green-400">${revenue.total.toFixed(2)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Track Sales</p>
              <p className="text-xl font-bold">${revenue.track_sales.toFixed(2)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Album Sales</p>
              <p className="text-xl font-bold">${revenue.album_sales.toFixed(2)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Merch Sales</p>
              <p className="text-xl font-bold">${revenue.merch_sales.toFixed(2)}</p>
            </div>
          </div>

          {/* Revenue Breakdown Chart */}
          <div className="space-y-3">
            {revenue.breakdown.map(item => {
              const percentage = revenue.total > 0 ? (item.amount / revenue.total) * 100 : 0;
              return (
                <div key={item.category} className="flex items-center gap-3">
                  <div className="w-32 text-sm text-gray-400">{item.category}</div>
                  <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%`, backgroundColor: item.color }}
                    />
                  </div>
                  <div className="w-24 text-right font-medium">${item.amount.toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Users} 
            label="Total Users" 
            value={users.total}
            subValue={`+${users.recent_signups} this month`}
            color="bg-blue-500"
          />
          <StatCard 
            icon={TrendingUp} 
            label="Premium Subs" 
            value={users.premium}
            subValue={`${users.subscribers} total subscribers`}
            color="bg-purple-500"
          />
          <StatCard 
            icon={PlayCircle} 
            label="Total Plays" 
            value={engagement.total_plays.toLocaleString()}
            subValue={`${engagement.recent_purchases} recent purchases`}
            color="bg-pink-500"
          />
          <StatCard 
            icon={Eye} 
            label="Video Views" 
            value={engagement.video_views.toLocaleString()}
            color="bg-red-500"
          />
        </div>

        {/* Content & Token Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Content Stats */}
          <div className="glass-card p-6" data-testid="content-stats">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Music className="w-5 h-5 text-purple-400" /> Content Library
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <Music className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                <p className="text-2xl font-bold">{content.tracks}</p>
                <p className="text-sm text-gray-400">Tracks</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <Music className="w-6 h-6 mx-auto mb-2 text-cyan-400" />
                <p className="text-2xl font-bold">{content.albums}</p>
                <p className="text-sm text-gray-400">Albums</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <Video className="w-6 h-6 mx-auto mb-2 text-pink-400" />
                <p className="text-2xl font-bold">{content.videos}</p>
                <p className="text-sm text-gray-400">Videos</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <ShoppingBag className="w-6 h-6 mx-auto mb-2 text-orange-400" />
                <p className="text-2xl font-bold">{content.merch_items}</p>
                <p className="text-sm text-gray-400">Merch Items</p>
              </div>
            </div>
          </div>

          {/* Token Stats */}
          <div className="glass-card p-6" data-testid="token-stats">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-400" /> Token Economy
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400">Tokens Sold</span>
                <span className="font-bold">{tokens.total_sold}%</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400">Unique Holders</span>
                <span className="font-bold">{tokens.unique_holders}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400">Active Listings</span>
                <span className="font-bold">{tokens.active_listings}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                <span className="text-yellow-400">Marketplace Volume</span>
                <span className="font-bold text-yellow-400">${tokens.marketplace_volume.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Tracks */}
          <div className="glass-card p-6" data-testid="top-tracks">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" /> Top Tracks by Plays
            </h2>
            <div className="space-y-3">
              {top_tracks && top_tracks.length > 0 ? top_tracks.map((track, idx) => (
                <div key={track.id} className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </span>
                  <img src={track.cover_url} alt={track.title} className="w-10 h-10 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{track.title}</p>
                    <p className="text-sm text-gray-400">{track.plays.toLocaleString()} plays</p>
                  </div>
                </div>
              )) : (
                <p className="text-gray-400 text-center py-4">No track data yet</p>
              )}
            </div>
          </div>

          {/* Top Merch */}
          <div className="glass-card p-6" data-testid="top-merch">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-orange-400" /> Top Selling Merch
            </h2>
            <div className="space-y-3">
              {top_merch && top_merch.length > 0 ? top_merch.map((item, idx) => (
                <div key={item.id} className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </span>
                  <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.name}</p>
                    <p className="text-sm text-gray-400">{item.sales_count} sold</p>
                  </div>
                </div>
              )) : (
                <p className="text-gray-400 text-center py-4">No merch data yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
